import React, { useMemo } from "react";
import { Box, Container } from "@mui/material";
import type { ContainerProps } from "@mui/material/Container";
import { ThemeProvider } from "@mui/material/styles";
import { useColorMode } from "../../contexts/ColorModeContext";
import { createAppTheme } from "../../theme";
import AuthNavbar, { type AuthNavbarVariant } from "../AuthNavbar";
import { authCenterColumnSx, authShellSx } from "./authScreenStyles";

export interface AuthScreenShellProps {
  children: React.ReactNode;
  /** Default `sm` matches sign-in / forgot-password. Use `md` for wide invite flows. */
  maxWidth?: ContainerProps["maxWidth"];
  /** When false, children fill the container without vertical centering (rare). */
  centerContent?: boolean;
  /** `landing` matches packages/frontend MainNavbar (logo only on invite). */
  navbarVariant?: AuthNavbarVariant;
}

const AuthScreenShell: React.FC<AuthScreenShellProps> = ({
  children,
  maxWidth = "sm",
  centerContent = true,
  navbarVariant = "landing",
}) => {
  const { mode } = useColorMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const centerColumnSx =
    navbarVariant === "landing"
      ? { ...authCenterColumnSx, pt: 4 }
      : authCenterColumnSx;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={authShellSx}>
        <AuthNavbar variant={navbarVariant} />
        <Container
          maxWidth={maxWidth}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {centerContent ? (
            <Box sx={centerColumnSx}>{children}</Box>
          ) : (
            children
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AuthScreenShell;
